import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AITaskProvider, useAITasks } from "./contexts/AITaskContext";
import Layout from "./components/Layout";
import GlobalMusicPlayer from "./components/GlobalMusicPlayer";

import Home from "./pages/Home";
import Music from "./pages/Music";
import Radio from "./pages/Radio";
import Chat from "./pages/Chat";
import Gallery from "./pages/Gallery";
import Videos from "./pages/Videos";
import News from "./pages/News";
import ArticleDetail from "./pages/ArticleDetail";
import About from "./pages/About";
import AIStudio from "./pages/AIStudio";
import Login from "./pages/Login";
import Settings from "./pages/Settings";
import Admin from "./pages/Admin";
import Events from "./pages/Events";
import SubmitArticle from "./pages/SubmitArticle";

const AdminRoute = ({ children, permission }) => {
  const { isAdmin, hasPermission, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (permission && !hasPermission(permission)) {
    return <Navigate to="/admin" replace />;
  }
  
  return children;
};

function GlobalTaskIndicator() {
  const { activeTasks } = useAITasks();
  const navigate = useNavigate();
  
  const processing = activeTasks.filter(t => 
    t.status === 'processing' || t.status === 'pending' || t.status === 'in_queue'
  );
  
  if (processing.length === 0) return null;
  
  const currentPath = window.location.pathname;
  if (currentPath === '/admin/ai-studio') return null;
  
  return (
    <div 
      onClick={() => navigate('/admin/ai-studio')}
      style={{
        position: 'fixed',
        bottom: '80px',
        right: '20px',
        zIndex: 9999,
        background: 'linear-gradient(135deg, rgba(0,255,255,0.15), rgba(0,200,255,0.2))',
        border: '1px solid rgba(0,255,255,0.4)',
        borderRadius: '12px',
        padding: '10px 16px',
        cursor: 'pointer',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        boxShadow: '0 4px 20px rgba(0,255,255,0.2)',
        animation: 'pulse 2s infinite',
        maxWidth: '300px',
      }}
    >
      <div style={{
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        background: '#00ffff',
        animation: 'pulse 1.5s infinite',
        flexShrink: 0,
      }} />
      <div>
        <div style={{ 
          color: '#00ffff', 
          fontSize: '12px', 
          fontFamily: 'Orbitron, sans-serif',
          fontWeight: 600,
        }}>
          AI GENERATING
        </div>
        <div style={{ 
          color: 'rgba(255,255,255,0.7)', 
          fontSize: '11px',
          fontFamily: 'Rajdhani, sans-serif',
        }}>
          {processing.length} task{processing.length > 1 ? 's' : ''} in progress
        </div>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { theme } = useTheme();
  
  return (
    <>
      <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/music" element={<Music />} />
          <Route path="/radio" element={<Radio />} />
          <Route path="/chat" element={<Chat />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/videos" element={<Videos />} />
          <Route path="/news" element={<News />} />
          <Route path="/news/:articleId" element={<ArticleDetail />} />
          <Route path="/events" element={<Events />} />
          <Route path="/submit-article" element={<SubmitArticle />} />
          <Route path="/about" element={<About />} />
          <Route path="/login" element={<Login />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/ai-studio" element={
            <AdminRoute permission="ai_studio">
              <AIStudio />
            </AdminRoute>
          } />
        </Routes>
      </Layout>
      <GlobalTaskIndicator />
      <GlobalMusicPlayer theme={theme} />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster 
            position="top-right" 
            toastOptions={{
              style: {
                background: 'rgba(0, 0, 0, 0.9)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: '#fff',
              },
            }}
          />
          <AITaskProvider>
            <AppRoutes />
          </AITaskProvider>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
